import React from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Controller } from 'react-hook-form';
import Images from '../Utils/images';

interface Props {
  id: string;
  label?: string;
  name: string;
  disabled?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  control?: null | any;
  helperText?: string;
  isError?: boolean;
  labelColor?: any;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'dander';
  labelSize?: 'small' | 'medium' | 'large'; //14 - 16- 18
  theme?: 'primary' | 'white' | 'dual' | 'circle';
}

/**
 * A customizable checkbox component with dynamic styles and control integration.
 * @component
 * @example
 *   <CheckBox
 *     id="checkbox1"
 *     label="Example Label"
 *     name="example"
 *     checked={true}
 *     disabled={false}
 *     onChange={handleCheckboxChange}
 *     color="primary"
 *     theme="circle"
 *   />
 * @prop {string} id - Unique identifier for the checkbox component.
 * @prop {string} label - Text label displayed next to the checkbox.
 * @prop {string} name - Name attribute for the checkbox input element.
 * @prop {boolean} disabled - Flag to disable the checkbox interaction.
 * @prop {boolean} checked - Determines if the checkbox is checked.
 * @prop {boolean} indeterminate - Sets the checkbox to an indeterminate state.
 * @prop {function} onChange - Callback function to handle change events.
 * @prop {string} value - Value attribute assigned to the checkbox input element.
 * @prop {object} control - Controller instance from React Hook Form for integration.
 * @prop {boolean} isError - Indicates whether an error state should be displayed.
 * @prop {string} helperText - Text providing additional context or instructions.
 * @prop {string} labelColor - Custom color for the label text, defaults to black.
 * @prop {string} color - Color scheme for the checkbox's appearance.
 * @prop {string} labelSize - Specifies the size of the label text, defaults to 'small'.
 * @prop {string} theme - Theme style for the checkbox, such as 'dual' or 'circle'.
 * @description
 *   - Supports integration with React Hook Form via the `control` prop for controlled checkbox state.
 *   - Utilizes dynamic class names and inline styles to accommodate different themes and sizes.
 *   - Can render error state style adjustments based on boolean flag `isError`.
 */
const InputCheckbox: React.FC<Props> = props => {
  const {
    id,
    label,
    name,
    disabled,
    checked,
    indeterminate,
    onChange,
    value,
    control,
    isError,
    helperText,
    labelColor = '#000000',
    color,
    labelSize = 'small',
    theme,
  } = props;
  return (
    <FormGroup>
      <FormControlLabel
        label={
          <span
            className={`switchLabel ${
              labelSize === 'small'
                ? 'small'
                : labelSize === 'medium'
                ? 'medium'
                : 'large'
            } ${theme === 'dual' ? 'check_dual' : ''}`}
            style={{
              color: labelColor,
            }}
          >
            {label}
          </span>
        }
        id={id}
        name={name}
        className={`checkbox ${isError ? 'checkError' : ''} ${
          color ? `check_${color}` : ''
        }`}
        control={
          control ? (
            <Controller
              control={control}
              name={name}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  disableRipple
                  {...field}
                  icon={
                    <img
                      src={
                        isError
                          ? Images.CHECK_RED
                          : theme === 'dual'
                          ? Images.CHECK
                          : theme === 'circle'
                          ? Images.Check_Circle_Outlined
                          : Images.UNCHECK_BLACK
                      }
                    />
                  }
                  checkedIcon={
                    <img
                      src={
                        theme === 'circle'
                          ? Images.Check_Circle_Filled
                          : Images.CHECK_TICK
                      }
                    />
                  }
                  className={`switchLabel ${
                    labelSize === 'small'
                      ? 'small'
                      : labelSize === 'medium'
                      ? 'medium'
                      : 'large'
                  }`}
                />
              )}
            />
          ) : (
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              disabled={disabled}
              onChange={onChange}
              disableRipple
              value={value}
              icon={
                <img
                  src={
                    isError
                      ? Images.CHECK_RED
                      : theme === 'dual'
                      ? Images.CHECK
                      : theme === 'circle'
                      ? Images.Check_Circle_Outlined
                      : Images.UNCHECK_BLACK
                  }
                />
              }
              checkedIcon={
                <img
                  src={
                    theme === 'circle'
                      ? Images.Check_Circle_Filled
                      : Images.CHECK_TICK
                  }
                />
              }
            />
          )
        }
      >
        {/* <FormHelperText className={isError ? 'Mui-error' : 'Mui-error'}>
          some error
        </FormHelperText> */}
      </FormControlLabel>
      {/* <FormHelperText className={isError ? 'Mui-error' : ''}>
        {helperText}
      </FormHelperText> */}
    </FormGroup>
  );
};

InputCheckbox.defaultProps = {
  disabled: false,
  indeterminate: false,
  label: '',
};

export default InputCheckbox;
